import React from 'react';

import { type Path, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { type CreateReservationTypeFormSchemaValues } from 'src/features/reservations/hooks/useCreateReservationType';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { DialogTitle } from 'src/registry/ui/Dialog';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Separator } from 'src/registry/ui/Separator';
import { generalMessages } from 'src/utils/messages';

const ReservationStayDetails = () => {
    const { control, setValue, watch } = useFormContext();
    const { formatMessage } = useIntl();

    const ExitEntryToleranceFormField = React.useCallback(
        ({ fieldName, label }: { fieldName: Path<CreateReservationTypeFormSchemaValues>; label: string }) => {
            return (
                <FormField
                    control={control}
                    name={fieldName}
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">{label}</FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Input
                                        type="text"
                                        placeholder="0"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={field.value ?? ''}
                                        onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            );
        },
        [control],
    );

    const ReserveSpot = {
        [formatMessage(generalMessages.no)]: 'false',
        [formatMessage(generalMessages.yes)]: 'true',
    };

    return (
        <>
            <DialogTitle>{formatMessage(reservationMessages.stayRules)}</DialogTitle>
            <Separator className="mb-4" />
            <FormField
                control={control}
                name="allowed_spaces"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(reservationMessages.allowedSpots)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="number"
                                    min={0}
                                    value={field.value ?? 0}
                                    onChange={(e) => field.onChange(Number(e.target.value))}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <ExitEntryToleranceFormField
                fieldName="entry_tolerance_minutes"
                label={formatMessage(reservationMessages.entryTolerance)}
            />
            <ExitEntryToleranceFormField
                fieldName="exit_tolerance_minutes"
                label={formatMessage(reservationMessages.exitTolerance)}
            />
            <FormField
                control={control}
                name="reserve_space"
                render={() => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">{formatMessage(reservationMessages.reservedSpot)}</FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Select
                                        onValueChange={(val) =>
                                            setValue('reserve_space', val === 'true', { shouldDirty: true })
                                        }
                                        value={String(watch('reserve_space'))}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue
                                                placeholder={formatMessage(reservationMessages.reserveSpotPlaceholder)}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(ReserveSpot).map(([key, value]) => {
                                                return (
                                                    <SelectItem key={key} value={value}>
                                                        {key}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
        </>
    );
};

export default ReservationStayDetails;
